import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { useMeQuery } from '../../services/authContext';
import './Profile.scss';
import { getVersion, getBuildNumber } from '../../config/appConfig';

function Profile() {
  const { accounts, instance } = useMsal();
  const user = accounts && accounts[0];
  const { isAdmin, isDeveloper } = useMeQuery();
  
  // State for tokens
  const [accessToken, setAccessToken] = useState(null); // API token
  const [tokenLoading, setTokenLoading] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Function to acquire both API and Graph tokens
  const acquireToken = async () => {
    if (!user) return;
    setTokenLoading(true);
    try {
      let apiToken;
      try {
        const response = await instance.acquireTokenSilent({ account: user, scopes: loginRequest.scopes });
        apiToken = response.accessToken;
      } catch (error) {
        console.error('API token acquisition failed:', error);
        const response = await instance.acquireTokenPopup({ account: user, scopes: loginRequest.scopes });
        apiToken = response.accessToken;
      }
      setAccessToken(apiToken);
    } finally {
      setTokenLoading(false);
    }
  };

  // Function to copy token to clipboard
  const copyToClipboard = async () => {
    if (!accessToken) return;
    try {
      await navigator.clipboard.writeText(accessToken);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy token:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = accessToken;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };


  // Load token on component mount
  useEffect(() => {
    if (user) {
      acquireToken();
    }
  }, [user]);

  if (!user) {
    return <div>Please log in to view your profile.</div>;
  }

  const version = getVersion();
  const buildNumber = getBuildNumber();

  return (
    <div className="profile-root">
      <div className="profile-panel">
        <div className="profile-header">
          <span className="profile-header-icon">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <circle cx="24" cy="16" r="10" stroke="#0984e3" strokeWidth="2.5" />
              <path d="M8 40c0-5.5 7-8.5 16-8.5s16 3 16 8.5" stroke="#0984e3" strokeWidth="2.5" />
            </svg>
          </span>
          <h2 className="profile-title">User Profile</h2>
          <button
            className="logout-btn"
            style={{
              marginLeft: 'auto',
              padding: '8px 18px',
              backgroundColor: '#e74c3c',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontWeight: 600,
              fontSize: '15px',
              cursor: 'pointer',
              transition: 'background 0.2s',
            }}
            onClick={() => {
              Object.keys(sessionStorage).forEach(k => { if (k.includes('interaction.status')) sessionStorage.removeItem(k); });
              instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
            }}
          >
            Log Out
          </button>
        </div>
        <div className="profile-field">
          <strong>App Version:</strong> {version}
        </div>
        <div className="profile-field">
          <strong>Build Number:</strong> {buildNumber}
        </div>
        <div className="profile-field">
          <strong>Name:</strong> {user.name || user.username}
        </div>
        <div className="profile-field">
          <strong>Email:</strong> {user.username}
        </div>
        <div className="profile-field">
          <strong>MS ID (OID):</strong> {user.localAccountId || 'N/A'}
        </div>
        <div className="profile-field">
          <strong>Tenant ID:</strong> {user.tenantId || 'N/A'}
        </div>
        <div className={`profile-field ${isAdmin ? 'profile-admin' : 'profile-user'}`}>
          <strong>Admin Status:</strong> {isAdmin ? 'Admin' : 'User'}
        </div>
        <div className={`profile-field ${isDeveloper ? 'profile-developer' : 'profile-non-developer'}`}>
          <strong>Developer Status:</strong> {isDeveloper ? 'Developer' : 'Non-Developer'}
        </div>
        {/* Auth Token Section */}
        <div className="profile-section">
          <div className="profile-section-header">
            <strong>API Token</strong>
            <button
              className="token-refresh-btn"
              onClick={acquireToken}
              disabled={tokenLoading}
              style={{
                marginLeft: '10px',
                padding: '4px 8px',
                fontSize: '12px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: tokenLoading ? 'not-allowed' : 'pointer'
              }}
            >
              {tokenLoading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
          
          {(accessToken) ? (
            <div className="token-container">
              <div className="token-display">
                <label style={{ fontWeight: 600 }}>API Token:</label>
                <textarea
                  readOnly
                  value={accessToken || ''}
                  style={{
                    width: '100%',
                    height: '60px',
                    fontFamily: 'monospace',
                    fontSize: '11px',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: '#f8f9fa',
                    resize: 'vertical',
                    wordBreak: 'break-all',
                    marginBottom: '8px'
                  }}
                />
              </div>
              <div className="token-actions" style={{ marginTop: '8px' }}>
                <button
                  className="copy-btn"
                  onClick={copyToClipboard}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: copySuccess ? '#28a745' : '#17a2b8',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '14px'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                  </svg>
                  {copySuccess ? 'Copied!' : 'Copy API Token'}
                </button>           
              </div>
              <div className="token-info" style={{ 
                marginTop: '8px', 
                fontSize: '12px', 
                color: '#6c757d',
                padding: '8px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                border: '1px solid #e9ecef'
              }}>
                <strong>Usage:</strong> For API calls, use <code>Authorization: Bearer &lt;api_token&gt;</code>.
                <br />
                <strong>Note:</strong> Tokens expire periodically. Use the Refresh button to get new tokens.
              </div>
            </div>
          ) : (
            <div className="token-placeholder" style={{
              padding: '16px',
              backgroundColor: '#f8f9fa',
              border: '1px solid #e9ecef',
              borderRadius: '4px',
              color: '#6c757d',
              textAlign: 'center'
            }}>
              {tokenLoading ? 'Loading tokens...' : 'Click Refresh to load your tokens'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Profile;
